import React, { useState, useEffect, useRef } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import { useUser } from 'contexts/UserContext';
import { useBusinessType } from 'contexts/BusinessType';
// Custom components
import BankReconciliation from '../utility/components/BankReconciliation';

// Assets (your image imports remain the same)
import img1 from 'assets/img/nfts/profitloss.svg';
import img2 from 'assets/img/nfts/balance sheet.svg';
import img3 from 'assets/img/nfts/contacts payable.svg';
import img03 from 'assets/img/nfts/statement.svg';
import img4 from 'assets/img/nfts/expenses.svg';
import img5 from 'assets/img/nfts/income.svg';
import img6 from 'assets/img/nfts/purchase detail.svg';
import img7 from 'assets/img/nfts/purchase summary.svg';
import img8 from 'assets/img/nfts/sale detail.svg';
import img9 from 'assets/img/nfts/sale summary.svg';
import img10 from 'assets/img/nfts/item by contact.svg';
import img11 from 'assets/img/nfts/stock.svg';
import img12 from 'assets/img/nfts/retail.svg';
import img13 from 'assets/img/nfts/tds report.svg';
import img14 from 'assets/img/nfts/gstr1.svg';
import img15 from 'assets/img/nfts/gstr3b.svg';
import img16 from 'assets/img/nfts/Item + Serialwise History.svg';
import img17 from 'assets/img/nfts/Serial Wise Bill To Bill Profit.svg';
import img20 from 'assets/img/nfts/Serial Wise Bill To Bill Profit.svg';
import img18 from 'assets/img/nfts/Serial Stock.svg';
import img19 from 'assets/img/nfts/Party Stock.svg';
import { useGlobal } from 'contexts';

// Skeleton Loader Components
const ReportCardSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';

   return (
      <div
         className="group rounded-2xl overflow-hidden transition-all duration-300 border-2"
         style={{
            backgroundColor: cardBg,
            borderColor: borderColor,
         }}>
         <div className="p-6">
            {/* Header */}
            <div className="flex items-start mb-4">
               <div
                  className="w-14 h-14 rounded-xl border-2 mr-4"
                  style={{
                     backgroundColor: isDark ? '#4a5568' : '#f7fafc',
                     borderColor: borderColor,
                  }}>
                  <div className={`w-full h-full rounded-xl ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
               <div className="flex-1">
                  <div
                     className={`h-6 rounded mb-2 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                     style={{ width: '80%' }}
                  />
                  <div className={`h-4 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '90%' }} />
                  <div
                     className={`h-4 rounded mt-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                     style={{ width: '70%' }}
                  />
               </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${borderColor}` }}>
               <div>
                  <div className={`h-3 w-12 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
               <div className={`w-5 h-5 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
            </div>
         </div>

         {/* Hover Effect Bar */}
         <div className={`h-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
      </div>
   );
};

const TabSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';

   return (
      <div
         className="rounded-2xl p-2 mb-6"
         style={{
            backgroundColor: cardBg,
            border: `1px solid ${borderColor}`,
         }}>
         <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
               <div
                  key={index}
                  className="px-4 py-3 rounded-xl border-2"
                  style={{
                     borderColor: borderColor,
                  }}>
                  <div className={`h-6 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
               </div>
            ))}
         </div>
      </div>
   );
};

const HeaderSkeleton = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   return (
      <div className="flex mb-3">
         <div className="flex-1">
            <div className={`h-8 rounded mb-2 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '250px' }} />
            <div className={`h-4 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`} style={{ width: '300px' }} />
         </div>
      </div>
   );
};

export default function Marketplace() {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { user } = useUser();
   const { businessTypes } = useBusinessType();
   const isTextile = businessTypes?.businessType === 'TEXTILE';
   const navigate = useNavigate();
   const { company, companyList } = useGlobal();
   const [isRenumberingTransection, setIsRenumberingTransection] = useState(false);
   const [isMobileBusiness, setIsMobileBusiness] = useState(false);
   const [activeTab, setActiveTab] = useState('accounting'); // Default value
   const [searchTerm, setSearchTerm] = useState('');
   const [isInitialized, setIsInitialized] = useState(false);
   const [isLoading, setIsLoading] = useState(true);

   // Refs for card elements
   const cardRefs = useRef({});
   const tabRefs = useRef({});

   const applyGST = company?.setting?.applyGST;
   const gstRegNo = company?.gstRegNo;

   // Check if GST reports should be shown
   const shouldShowGSTReports = applyGST && gstRegNo;

   // Initialize all states from localStorage
   useEffect(() => {
      const initializeFromStorage = () => {
         try {
            // Initialize active tab
            const savedTab = localStorage.getItem('activeReportTab');
            if (savedTab) {
               setActiveTab(savedTab);
            }

            // Initialize business type
            const businessType = localStorage.getItem('businessTypes');
            setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');

            setIsInitialized(true);
            setIsLoading(false);
         } catch (error) {
            console.error('Error reading localStorage:', error);
            setIsInitialized(true);
            setIsLoading(false);
         }
      };

      initializeFromStorage();
   }, []);

   // Save active tab to localStorage whenever it changes
   useEffect(() => {
      if (isInitialized && typeof window !== 'undefined') {
         localStorage.setItem('activeReportTab', activeTab);
      }
   }, [activeTab, isInitialized]);

   // Listen for business type changes
   useEffect(() => {
      if (!isInitialized) return;

      const handleStorageChange = (e) => {
         if (e.key === 'businessTypes') {
            setIsMobileBusiness(e.newValue === 'MOBILE' || e.newValue === 'mobile');
         }
      };

      const handleVisibilityChange = () => {
         if (!document.hidden) {
            try {
               const businessType = localStorage.getItem('businessTypes');
               setIsMobileBusiness(businessType === 'MOBILE' || businessType === 'mobile');
            } catch (error) {
               console.error('Error updating businessType:', error);
            }
         }
      };

      window.addEventListener('storage', handleStorageChange);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
         window.removeEventListener('storage', handleStorageChange);
         document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
   }, [isInitialized]);

   const handleCloseModals = () => {
      setIsRenumberingTransection(false);
   };

   const handleOpenBankReconciliation = () => {
      setIsRenumberingTransection(true);
   };

   const handleRenumberingTransection = () => {
      // Implementation for handling renumbering transaction
   };

   // Tab order for keyboard navigation
   const tabKeys = ['accounting', 'purchase', 'inventory', 'tax'];

   // Focus the active tab after initialization
   useEffect(() => {
      if (isInitialized && !isLoading) {
         const savedTab = localStorage.getItem('activeReportTab') || 'accounting';
         setTimeout(() => {
            document.getElementById(`tab-btn-${savedTab}`)?.focus();
         }, 100);
      }
   }, [isInitialized, isLoading]);

   const handleTabKeyDown = (e, currentKey) => {
      if (e.key === 'Tab') {
         e.preventDefault();
         const currentIndex = tabKeys.indexOf(currentKey);
         const nextIndex = e.shiftKey
            ? (currentIndex - 1 + tabKeys.length) % tabKeys.length   // Shift+Tab goes backward
            : (currentIndex + 1) % tabKeys.length;                   // Tab goes forward
         const nextKey = tabKeys[nextIndex];
         setActiveTab(nextKey);
         // Move focus to the next button
         document.getElementById(`tab-btn-${nextKey}`)?.focus();
      } else if (e.key === 'ArrowDown') {
         e.preventDefault();
         // Move focus to the first card in the current tab
         if (filteredItems.length > 0) {
            const firstItem = filteredItems[0];
            const firstCardId = getCardId(firstItem);
            // Small timeout to ensure the DOM is updated
            setTimeout(() => {
               cardRefs.current[firstCardId]?.focus();
            }, 50);
         }
      }
   };

   // Handle card keyboard navigation
   const handleCardKeyDown = (e, item, index) => {
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         handleCardClick(item);
      } else if (e.key === 'Tab') {
         // Let default Tab behavior work for moving between cards
         if (e.shiftKey) {
            // If it's the first card and Shift+Tab, go back to the active tab
            if (index === 0) {
               e.preventDefault();
               document.getElementById(`tab-btn-${activeTab}`)?.focus();
            }
         } else {
            // If it's the last card and Tab, let it go to next element after cards
            if (index === filteredItems.length - 1) {
               // Let default behavior happen
               return;
            }
         }
      } else if (e.key === 'ArrowDown') {
         e.preventDefault();
         // Move to next card
         const nextIndex = index + 1;
         if (nextIndex < filteredItems.length) {
            const nextItem = filteredItems[nextIndex];
            const nextCardId = getCardId(nextItem);
            cardRefs.current[nextCardId]?.focus();
         }
      } else if (e.key === 'ArrowUp') {
         e.preventDefault();
         // Move to previous card
         const prevIndex = index - 1;
         if (prevIndex >= 0) {
            const prevItem = filteredItems[prevIndex];
            const prevCardId = getCardId(prevItem);
            cardRefs.current[prevCardId]?.focus();
         } else {
            // If at first card and pressing ArrowUp, go back to the active tab
            document.getElementById(`tab-btn-${activeTab}`)?.focus();
         }
      }
   };

   // Helper function to generate unique card ID
   const getCardId = (item) => {
      return `report-card-${item.name.replace(/\s+/g, '-').toLowerCase()}`;
   };

   // Navigation handler
   const handleReportNavigation = (reportName) => {
      const routeMap = {
         Statement: '/admin/report/Statement',
         Outstanding: '/admin/report/Outstanding',
         'Profit & Loss': '/admin/report/ProfitLoss',
         'Balance Sheet': '/admin/report/BalanceSheet',
         'Trial Balance': '/admin/report/TrialBalance',
         Expenses: '/admin/report/Expenses',
         Income: '/admin/report/Income',
         'Purchase Order Detail': '/admin/report/PurchaseOrderDetail',
         'Purchase Challan Detail': '/admin/report/PurchaseChallanDetail',
         'Purchase Invoice Detail': '/admin/report/PurchaseDetail',
         'Purchase Return Detail': '/admin/report/PurchaseReturnDetail',
         'Purchase Invoice Summary': '/admin/report/PurchaseSummary',
         'Purchase Return Summary': '/admin/report/PurchaseReturnSummary',
         'Sale Order Detail': '/admin/report/SaleOrderDetail',
         'Sale Challan Detail': '/admin/report/SaleChallanDetail',
         'Sale Invoice Detail': '/admin/report/SalesDetail',
         'Sale Return Detail': '/admin/report/SaleReturnDetail',
         'Sale Invoice Summary': '/admin/report/SaleSummary',
         'Sale Return Summary': '/admin/report/SaleReturnSummary',
         'Transaction Log': '/admin/report/TransactionHistory',
         'Mill Issue Detail': '/admin/report/MillIssueDetail',
         'Gray Purchase Detail': '/admin/report/GrayPurchaseDetail',
         Stock: '/admin/report/Stock',
         'Party Stock': '/admin/report/PartyStock',
         'All Transaction': '/admin/report/AllTransaction',
         SerialStock: '/admin/report/SerialStock',
         'Inventory Detail': '/admin/report/InventoryDetail',
         'Serial Wise Bill To Bill Profit': '/admin/report/SerialWiseBillToBillProfit',
         'None-Serialised Profit': '/admin/report/noneSerialisedProfit',
         'Item + Serial wise History': '/admin/report/ItemSerialwiseHistory',
         'TDS REPORT': '/admin/report/TDSREPORT',
         GSTR1: '/admin/report/GSTR1',
         GSTR3B: '/admin/report/GSTR3B',
         'GSTR 2A/B2B RECONCILATION': user?.packageData?.[0]?.is2a2b ? '/admin/report/GSTR2AB2BReconcilation' : null,
      };

      const route = routeMap[reportName];
      if (route) {
         navigate(route);
      } else {
         console.warn(`No route defined for report: ${reportName}`);
      }
   };

   // Card click handler
   const handleCardClick = (item) => {
      if (item.isBankRecon) {
         handleOpenBankReconciliation();
      } else {
         handleReportNavigation(item.name);
      }
   };

   // Dynamic styles based on color mode
   const bgColor = isDark ? '#1a202c' : '#f7fafc';
   const cardBg = isDark ? '#2d3748' : '#ffffff';
   const textColor = isDark ? '#e2e8f0' : '#1a202c';
   const borderColor = isDark ? '#4a5568' : '#e2e8f0';
   const shadowColor = isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)';
   const scrollbarThumb = isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';

   // Enhanced color system with proper dark mode support
   const getColorClasses = (color) => {
      const colorMap = {
         blue: {
            gradient: 'from-blue-500 to-blue-600',
            bg: isDark ? 'bg-blue-900/20' : 'bg-blue-50',
            text: isDark ? 'text-blue-300' : 'text-blue-600',
            border: isDark ? 'border-blue-700' : 'border-blue-200',
            activeBg: 'linear-gradient(135deg, #4299e1, #667eea)',
            focusRing: isDark ? '0 0 0 3px rgba(66, 153, 225, 0.5)' : '0 0 0 3px rgba(66, 153, 225, 0.5)',
         },
         emerald: {
            gradient: 'from-emerald-500 to-emerald-600',
            bg: isDark ? 'bg-emerald-900/20' : 'bg-emerald-50',
            text: isDark ? 'text-emerald-300' : 'text-emerald-600',
            border: isDark ? 'border-emerald-700' : 'border-emerald-200',
            activeBg: 'linear-gradient(135deg, #48bb78, #38a169)',
            focusRing: isDark ? '0 0 0 3px rgba(72, 187, 120, 0.5)' : '0 0 0 3px rgba(72, 187, 120, 0.5)',
         },
         violet: {
            gradient: 'from-violet-500 to-violet-600',
            bg: isDark ? 'bg-violet-900/20' : 'bg-violet-50',
            text: isDark ? 'text-violet-300' : 'text-violet-600',
            border: isDark ? 'border-violet-700' : 'border-violet-200',
            activeBg: 'linear-gradient(135deg, #9f7aea, #805ad5)',
            focusRing: isDark ? '0 0 0 3px rgba(159, 122, 234, 0.5)' : '0 0 0 3px rgba(159, 122, 234, 0.5)',
         },
         amber: {
            gradient: 'from-amber-500 to-amber-600',
            bg: isDark ? 'bg-amber-900/20' : 'bg-amber-50',
            text: isDark ? 'text-amber-300' : 'text-amber-600',
            border: isDark ? 'border-amber-700' : 'border-amber-200',
            activeBg: 'linear-gradient(135deg, #ed8936, #dd6b20)',
            focusRing: isDark ? '0 0 0 3px rgba(237, 137, 54, 0.5)' : '0 0 0 3px rgba(237, 137, 54, 0.5)',
         },
      };
      return colorMap[color] || colorMap.blue;
   };

   // GST reports array - conditionally included
   const gstReports =
      shouldShowGSTReports && user?.packageData?.[0]?.is2a2b
         ? [
            { name: 'GSTR1', image: img14, desc: 'Outward supplies return', frequency: 'Monthly' },
            { name: 'GSTR3B', image: img15, desc: 'Summary GST return', frequency: 'Monthly' },
            { name: 'GSTR 2A/B2B RECONCILATION', image: img15, desc: 'GST reconciliation', frequency: 'Monthly' },
         ]
         : shouldShowGSTReports
            ? [
               { name: 'GSTR1', image: img14, desc: 'Outward supplies return', frequency: 'Monthly' },
               { name: 'GSTR3B', image: img15, desc: 'Summary GST return', frequency: 'Monthly' },
            ]
            : [];

   // Module data structure
   const modules = {
      accounting: {
         title: 'Accounting',
         color: 'blue',
         items: [
            { name: 'Statement', image: img03, desc: 'View detailed account statements', frequency: 'Daily' },
            { name: 'Outstanding', image: img3, desc: 'Track pending receivables', frequency: 'Weekly' },
            { name: 'All Transaction', image: img18, desc: 'Complete inventory log', frequency: 'Daily' },
            {
               name: 'Profit & Loss',
               image: img1,
               desc: 'Income vs expenses analysis',
               frequency: 'Monthly',
               imageWidth: '100px',
               imageHeight: '100px',
            },
            { name: 'Balance Sheet', image: img2, desc: 'Assets, liabilities & equity', frequency: 'Monthly' },
            { name: 'Trial Balance', image: img2, desc: 'Verify accounting accuracy', frequency: 'Monthly' },
         ],
      },
      purchase: {
         title: 'Purchase & Sales',
         color: 'emerald',
         items: [
            ...(isTextile ? [
               { name: 'Purchase Order Detail', image: img6, desc: 'Complete purchase records', frequency: 'Daily' },
               { name: 'Purchase Challan Detail', image: img6, desc: 'Complete purchase records', frequency: 'Daily' },
            ] : []),
            { name: 'Purchase Invoice Detail', image: img6, desc: 'Complete purchase records', frequency: 'Daily' },
            { name: 'Purchase Return Detail', image: img6, desc: 'Return transactions', frequency: 'Weekly' },
            { name: 'Purchase Invoice Summary', image: img7, desc: 'Overview of all purchases', frequency: 'Monthly' },
            { name: 'Purchase Return Summary', image: img7, desc: 'Return summary', frequency: 'Monthly' },
            ...(isTextile ? [
               { name: 'Sale Order Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
               { name: 'Sale Challan Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
            ] : []),
            { name: 'Sale Invoice Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
            { name: 'Sale Return Detail', image: img8, desc: 'Handle customer returns', frequency: 'Weekly' },
            { name: 'Sale Invoice Summary', image: img9, desc: 'Comprehensive sales overview', frequency: 'Monthly' },
            { name: 'Sale Return Summary', image: img9, desc: 'Return analytics', frequency: 'Monthly' },
            ...(isTextile ? [
               { name: 'Mill Issue Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
               { name: 'Gray Purchase Detail', image: img8, desc: 'Complete sales records', frequency: 'Daily' },
            ] : []),
            {
               name: 'Bank Reconciliation',
               image: img10,
               desc: 'Match bank transactions',
               frequency: 'Monthly',
               isBankRecon: true,
            },
            { name: 'Transaction Log', image: img9, desc: 'All financial transactions', frequency: 'Daily' },
         ],
      },
      inventory: {
         title: 'Inventory',
         color: 'violet',
         items: [
            { name: 'Stock', image: img11, desc: 'Current stock levels', frequency: 'Real-time' },
            ...(isMobileBusiness
               ? [{ name: 'Party Stock', image: img19, desc: 'Stock by party/vendor', frequency: 'Daily' }]
               : []),
            { name: 'SerialStock', image: img18, desc: 'Serial number tracking', frequency: 'Real-time' },
            { name: 'Inventory Detail', image: img12, desc: 'Detailed stock analysis', frequency: 'Weekly' },
            ...(isMobileBusiness ? [
               {
                  name: 'Serial Wise Bill To Bill Profit',
                  image: img17,
                  desc: 'Serial-wise profitability',
                  frequency: 'Monthly',
               },
            ] : []),
            {
               name: 'None-Serialised Profit',
               image: img20,
               desc: 'None-Serialised Profitability',
               frequency: 'Monthly',
            },
            ...(isMobileBusiness ? [{ name: 'Item + Serial wise History', image: img16, desc: 'Item movement tracking', frequency: 'Daily' }] : []),
         ],
      },
      tax: {
         title: 'Tax & Compliance',
         color: 'amber',
         items: [
            { name: 'TDS REPORT', image: img13, desc: 'Tax Deducted at Source', frequency: 'Quarterly' },
            ...gstReports,
         ],
      },
   };

   const currentModule = modules[activeTab];
   const filteredItems = currentModule.items.filter(
      (item) =>
         item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         item.desc.toLowerCase().includes(searchTerm.toLowerCase()),
   );

   // Don't render until initialized to prevent flash of wrong tab
   if (!isInitialized) {
      return (
         <div className="pt-9 h-[calc(100vh-80px)] flex items-center justify-center">
            <div className="text-center">
               <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
               <p className="mt-4 text-gray-500">Loading reports...</p>
            </div>
         </div>
      );
   }

   return (
      <>
         <div className="pt-14 h-[calc(100vh-80px)] relative overflow-hidden transition-colors duration-300">
            <div
               className="h-full overflow-y-auto px-2.5 md:px-5"
               style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${scrollbarThumb} transparent`,
               }}>
               <style jsx>{`
                  div::-webkit-scrollbar {
                     width: 6px;
                  }
                  div::-webkit-scrollbar-track {
                     background: transparent;
                  }
                  div::-webkit-scrollbar-thumb {
                     background: ${scrollbarThumb};
                     border-radius: 10px;
                  }
                  div::-webkit-scrollbar-thumb:hover {
                     background: ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
                  }
               `}</style>

               {/* Page Header */}
               {isLoading ? (
                  <HeaderSkeleton />
               ) : (
                  <div className="flex mb-3">
                     <div className="flex-1">
                        <h2 className="text-3xl font-bold mb-2" style={{ color: textColor }}>
                           Reports & Analytics
                        </h2>
                        <p className="text-gray-500">Access your financial reports and business insights</p>
                     </div>
                  </div>
               )}

               {/* Navigation Tabs */}
               {isLoading ? (
                  <TabSkeleton />
               ) : (
                  <div
                     className="rounded-2xl p-2 mb-6"
                     style={{
                        backgroundColor: cardBg,
                        boxShadow: `0 2px 4px ${shadowColor}`,
                        border: `1px solid ${borderColor}`,
                     }}>
                     <div className="grid grid-cols-4 gap-2">
                        {Object.entries(modules).map(([key, module]) => {
                           const isActive = activeTab === key;
                           const colors = getColorClasses(module.color);

                           return (
                              <button
                                 key={key}
                                 id={`tab-btn-${key}`}
                                 ref={(el) => {
                                    if (el) {
                                       tabRefs.current[key] = el;
                                    }
                                 }}
                                 onClick={() => setActiveTab(key)}
                                 onKeyDown={(e) => handleTabKeyDown(e, key)}
                                 className={`px-4 py-3 rounded-xl transition-all font-semibold text-sm border-2 ${isActive
                                    ? 'text-white shadow-lg transform scale-[1.02]'
                                    : 'hover:scale-[1.01] hover:shadow-md'
                                    }`}
                                 style={{
                                    background: isActive ? colors.activeBg : 'transparent',
                                    color: isActive ? '#ffffff' : '#CBCBCB',
                                    borderColor: isActive ? colors.border : colors.border,
                                    borderWidth: '2px',
                                    outline: 'none',
                                 }}
                                 onFocus={(e) => {
                                    e.currentTarget.style.boxShadow = colors.focusRing;
                                 }}
                                 onBlur={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                 }}>
                                 {module.title}
                              </button>
                           );
                        })}
                     </div>
                  </div>
               )}

               {/* Reports Grid */}
               {isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                     {Array.from({ length: 8 }).map((_, index) => (
                        <ReportCardSkeleton key={index} />
                     ))}
                  </div>
               ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
                     {filteredItems.map((item, index) => {
                        const colors = getColorClasses(currentModule.color);
                        const cardId = getCardId(item);

                        return (
                           <div
                              key={index}
                              ref={(el) => {
                                 if (el) {
                                    cardRefs.current[cardId] = el;
                                 }
                              }}
                              id={cardId}
                              tabIndex={0} // Make the card focusable
                              role="button"
                              aria-label={`View ${item.name} report`}
                              className="group rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer border-2 focus:outline-none"
                              style={{
                                 backgroundColor: cardBg,
                                 boxShadow: `0 2px 4px ${shadowColor}`,
                                 borderColor: colors.border,
                              }}
                              onClick={() => handleCardClick(item)}
                              onKeyDown={(e) => handleCardKeyDown(e, item, index)}
                              onFocus={(e) => {
                                 e.currentTarget.style.boxShadow = colors.focusRing;
                                 e.currentTarget.style.transform = 'scale(1.02)';
                              }}
                              onBlur={(e) => {
                                 e.currentTarget.style.boxShadow = `0 2px 4px ${shadowColor}`;
                                 e.currentTarget.style.transform = 'scale(1)';
                              }}>
                              <div className="p-6">
                                 {/* Header */}
                                 <div className="flex items-start mb-4">
                                    <div
                                       className="w-14 h-14 rounded-xl flex items-center justify-center p-2 border-2 mr-4"
                                       style={{
                                          backgroundColor: colors.bg,
                                          borderColor: colors.border,
                                       }}>
                                       <img
                                          src={item.image}
                                          alt={item.name}
                                          className="w-full h-full object-contain"
                                          style={{
                                             width: item.imageWidth || 'auto',
                                             height: item.imageHeight || 'auto',
                                          }}
                                       />
                                    </div>
                                    <div>
                                       <h3
                                          className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors"
                                          style={{ color: textColor }}>
                                          {item.name}
                                       </h3>
                                       <p className={`text-sm ${isDark ? 'gray-600' : 'gray-300'} mb-2 line-clamp-2`}>
                                          {item.desc}
                                       </p>
                                    </div>
                                 </div>

                                 {/* Metadata */}
                                 <div
                                    className="flex items-center justify-between pt-4"
                                    style={{ borderTop: `1px solid ${borderColor}` }}>
                                    <div>
                                       {/* <p className="text-xs text-gray-500 mb-0.5">Frequency</p>
                                       <p className="text-sm font-medium" style={{ color: textColor }}>
                                          {item.frequency}
                                       </p> */}
                                    </div>
                                    <ChevronRight
                                       className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                                       size={20}
                                    />
                                 </div>
                              </div>

                              {/* Hover Effect Bar */}
                              <div
                                 className={`h-1 bg-gradient-to-r ${colors.gradient} transform scale-x-0 group-hover:scale-x-100 group-focus:scale-x-100 transition-transform origin-left`}></div>
                           </div>
                        );
                     })}
                  </div>
               )}

               {/* Empty State */}
               {!isLoading && filteredItems.length === 0 && (
                  <div
                     className="rounded-2xl p-12 text-center"
                     style={{
                        backgroundColor: cardBg,
                        boxShadow: `0 2px 4px ${shadowColor}`,
                        border: `1px solid ${borderColor}`,
                     }}>
                     <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: isDark ? '#4a5568' : '#f7fafc' }}>
                        <Search className="text-gray-400" size={32} />
                     </div>
                     <h3 className="text-lg font-semibold mb-2" style={{ color: textColor }}>
                        No results found
                     </h3>
                     <p className="text-gray-500">Try adjusting your search terms</p>
                  </div>
               )}
            </div>
         </div>

         <BankReconciliation
            isRenumberingTransection={isRenumberingTransection}
            onClose={handleCloseModals}
            handleRenumberingTransection={handleRenumberingTransection}
         />
      </>
   );
}